import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

export function AbdomenPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const liver = data.liver || {};
  const veins = data.veins || {};
  const gb = data.gallbladder || {};
  const cbd = data.commonBileDuct || {};
  const pancreas = data.pancreas || {};
  const spleen = data.spleen || {};
  const rk = data.rightKidney || {};
  const lk = data.leftKidney || {};

  const formatMasses = (masses) => (masses || [])
    .filter(Boolean)
    .map(mass => [mass.location, mass.echotexture, (mass.size1 && mass.size2 && mass.size3) ? `${mass.size1} x ${mass.size2} x ${mass.size3} cm` : null, mass.description]
      .filter(Boolean).join(', '))
    .join('; ');

  const formatStones = (stones) => (stones || [])
    .filter(Boolean)
    .map(stone => [stone.size ? `${stone.size} cm` : null, stone.description].filter(Boolean).join(', '))
    .join('; ');

  const measurementsText = [
    liver.size ? `Liver: ${liver.size} cm` : null,
    spleen.size ? `Spleen: ${spleen.size} cm` : null,
    gb.wallThickness ? `GB wall thickness: ${gb.wallThickness} mm` : null,
    cbd.measurement ? `Common bile duct: ${cbd.measurement} mm` : null,
    rk.length ? `Right kidney: ${rk.length} cm` : null,
    lk.length ? `Left kidney: ${lk.length} cm` : null,
  ].filter(Boolean).join('\n');

  const veinsNormal = veins.hepaticVeins === 'Patent' &&
                       veins.portalVein === 'Hepatopetal Flow' &&
                       veins.paraumbilicalVein === 'Not Visualized';

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="ABDOMINAL ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="MEASUREMENTS">
              <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
            </PdfMeasurementBox>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfAccentSectionHeader title="LIVER" />
            <PdfLabelValue label="Echotexture:" value={liver.echotexture || ''} isAbnormal={liver.echotexture && liver.echotexture !== 'Normal'} />
            {liver.masses && liver.masses.length > 0 && (
              <PdfLabelValue label="Masses/Lesions:" value={formatMasses(liver.masses)} isAbnormal />
            )}
            {liver.other && (
              <PdfLabelValue label="Additional findings:" value={liver.other} isAbnormal />
            )}

            <PdfAccentSectionHeader title="VEINS" />
            {veinsNormal ? (
              <PdfLabelValue label="Status:" value="Normal portal and hepatic veins" forceBlack />
            ) : (
              <>
                <PdfLabelValue label="Hepatic veins:" value={veins.hepaticVeins || ''} isAbnormal={veins.hepaticVeins && veins.hepaticVeins !== 'Patent'} />
                <PdfLabelValue label="Portal vein:" value={veins.portalVein || ''} isAbnormal={veins.portalVein && veins.portalVein !== 'Hepatopetal Flow'} />
                <PdfLabelValue label="Paraumbilical vein:" value={veins.paraumbilicalVein || ''} isAbnormal={veins.paraumbilicalVein && veins.paraumbilicalVein !== 'Not Visualized'} />
              </>
            )}
            {veins.other && (
              <PdfLabelValue label="Additional findings:" value={veins.other} isAbnormal />
            )}

            <PdfAccentSectionHeader title="GALLBLADDER" />
            {gb.status && gb.status.length > 0 && (
              <PdfLabelValue label="Status:" value={gb.status.filter(Boolean).join(', ')} isAbnormal={gb.status.some(s => s && s !== 'Normal')} />
            )}
            {gb.stones && gb.stones.length > 0 && (
              <PdfLabelValue label="Stones:" value={formatStones(gb.stones)} isAbnormal />
            )}
            {gb.other && (
              <PdfLabelValue label="Additional findings:" value={gb.other} isAbnormal />
            )}

            <PdfAccentSectionHeader title="COMMON BILE DUCT" />
            <PdfLabelValue label="Status:" value={cbd.status || ''} isAbnormal={cbd.status && !cbd.status.startsWith('Normal')} />

            <PdfAccentSectionHeader title="PANCREAS" />
            <PdfLabelValue label="Status:" value={pancreas.status || ''} isAbnormal={pancreas.status && pancreas.status !== 'Normal'} />
            {pancreas.other && (
              <PdfLabelValue label="Additional findings:" value={pancreas.other} isAbnormal />
            )}

            <PdfAccentSectionHeader title="SPLEEN" />
            <PdfLabelValue label="Status:" value={spleen.status || ''} isAbnormal={spleen.status && spleen.status !== 'Normal'} />
            {spleen.size && (
              <PdfLabelValue label="Size:" value={`${spleen.size} cm`} isAbnormal={parseFloat(spleen.size) > 12.5} />
            )}
            {spleen.other && (
              <PdfLabelValue label="Additional findings:" value={spleen.other} isAbnormal />
            )}

            <PdfAccentSectionHeader title="KIDNEYS" />
            {rk.status && rk.status.length > 0 && (
              <PdfLabelValue label="Right kidney:" value={rk.status.filter(Boolean).join(', ')} isAbnormal={rk.status.some(s => s && s !== 'Normal')} />
            )}
            {rk.masses && rk.masses.length > 0 && (
              <PdfLabelValue label="Right kidney masses:" value={formatMasses(rk.masses)} isAbnormal />
            )}
            {rk.other && (
              <PdfLabelValue label="Right kidney findings:" value={rk.other} isAbnormal />
            )}
            {lk.status && lk.status.length > 0 && (
              <PdfLabelValue label="Left kidney:" value={lk.status.filter(Boolean).join(', ')} isAbnormal={lk.status.some(s => s && s !== 'Normal')} />
            )}
            {lk.masses && lk.masses.length > 0 && (
              <PdfLabelValue label="Left kidney masses:" value={formatMasses(lk.masses)} isAbnormal />
            )}
            {lk.other && (
              <PdfLabelValue label="Left kidney findings:" value={lk.other} isAbnormal />
            )}

            <PdfAccentSectionHeader title="OTHER FINDINGS" />
            <PdfLabelValue label="Ascites:" value={data.ascites ? 'Present' : 'None'} isAbnormal={!!data.ascites} />
            {data.masses && data.masses.length > 0 && (
              <PdfLabelValue label="Abdominal masses:" value={formatMasses(data.masses)} isAbnormal />
            )}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
